import React, { FC, memo, useMemo } from 'react';
import { FiDownload } from 'react-icons/fi';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { checkedLocalization } from 'utils/checkedLocalization';
import css from '../styles.module.scss';
import { TScreenshotType } from '../types';
import { useScreenshotNotification } from '../hooks/useScreenshotNotification';

const ScreenshotNotification: FC<{
  notification: TScreenshotType;
  handleMarkAsRead: () => void;
}> = ({ notification, handleMarkAsRead }) => {
  const { i18n, t } = useTranslation();
  const { message } = notification;
  const {
    creative_internal_id,
    creative_xxhash,
    creative_title,
    screenshot_url,
    campaign_xxhash,
    loadedImg,
    setLoadedImg,
    saveScreenHandler,
  } = useScreenshotNotification({ message, handleMarkAsRead });

  if (message.status === 'ERROR') {
    const localisationCode = useMemo(() => {
      if (!('error_code' in message)) {
        return 'none';
      }

      return checkedLocalization(
        `errorsCreateScreenshot.${message.error_code}`,
        'none',
        false,
      );
    }, [message, i18n.language]);

    const showErrorCode = localisationCode !== 'none';
    const showErrorMessage = !showErrorCode && 'error_message' in message;

    return (
      <div className={css.item__text}>
        <span>{t(`screenshotCreationError`)} </span>
        <Link
          to={`/main/campaign/${campaign_xxhash}/creatives/${creative_xxhash}/basic_settings`}
          rel="noopener noreferrer"
          target="_blank"
          onClick={handleMarkAsRead}
        >
          <span className={css.item__link}>
            {`[${creative_internal_id}] ${creative_title}`}.
          </span>
        </Link>
        {showErrorCode && (
          <span className={css.gray}>{` ${localisationCode}`}</span>
        )}
        {showErrorMessage && (
          <span className={css.gray}>{` (${message.error_message})`}</span>
        )}
      </div>
    );
  }

  return (
    <div className={css.item__screenshot}>
      <div
        className={cn(css.item__screenshot_img, { [css.loaded]: loadedImg })}
      >
        <img
          src={screenshot_url}
          alt={creative_xxhash}
          onLoad={() => setLoadedImg(true)}
        />
        {!loadedImg && <div className={css.item__screenshot_placeholder} />}
      </div>

      <div className={css.item__screenshot_info}>
        <div className={css.item__text}>
          <span>{t(`screenshotCreated`)} </span>
          <Link
            to={`/main/campaign/${campaign_xxhash}/creatives/${creative_xxhash}/basic_settings`}
            rel="noopener noreferrer"
            target="_blank"
            onClick={handleMarkAsRead}
          >
            <span className={css.item__link}>
              {`[${creative_internal_id}] ${creative_title}`}.
            </span>
          </Link>
        </div>
        <div className={css.item__screenshot_actions}>
          <button
            type="button"
            className={css.item__screenshot_download}
            onClick={saveScreenHandler}
          >
            <FiDownload size={20} />
            <span>{t(`download_btn`)}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(ScreenshotNotification);
